import { ValidationResult, BaseValidatorOptions } from "./types";

/**
 * Options for phone validation
 */
export interface PhoneValidatorOptions extends BaseValidatorOptions {
  pattern?: RegExp;
}

/**
 * Validate a phone number value
 * Accepts various formats including: 1234567890, (123) 456-7890, +1234567890
 * Removes common formatting characters for validation
 * @param value - The phone number string to validate
 * @param options - Validation options (custom pattern)
 * @returns Validation result with the phone value and optional error, or null if invalid
 *
 * @example
 * ```typescript
 * validatePhone("1234567890");        // { value: "1234567890" }
 * validatePhone("(123) 456-7890");    // { value: "(123) 456-7890" }
 * validatePhone("+1234567890");       // { value: "+1234567890" }
 * validatePhone("abc123");            // null
 * ```
 */
export function validatePhone(value: string, options: PhoneValidatorOptions = {}): ValidationResult<string> | null {
  const { field = "value", pattern } = options;

  // Remove common formatting characters
  const normalized = value.replace(/[\s\-().]/g, "");

  // Check if it contains only digits and optional + prefix
  const phoneRegex = /^\+?\d{10,15}$/;

  if (!phoneRegex.test(normalized)) {
    return null;
  }

  // Check custom pattern if provided
  if (pattern && !pattern.test(value)) {
    return {
      value,
      error: {
        field,
        message: `"${field}" does not match required phone pattern: ${value}`,
        severity: "warning",
      },
    };
  }

  return { value };
}
